import type { StageBreakdown, StageLineItem } from "./types";

/**
 * The stage-bearing part of any pricing breakdown.
 *
 * Declared structurally rather than as a `Pick<BreakdownStructure, ...>` so the
 * quote, rental and public breakdowns all satisfy it — they carry the same
 * stages but narrow the tax item type differently.
 */
export interface StagedBreakdown {
  fees_subtotal?: StageBreakdown;
  fees_pre_tax?: StageBreakdown;
  taxes?: StageBreakdown;
  fees_post_tax?: StageBreakdown;
}

export type PricingStageKey =
  | "fees_subtotal"
  | "fees_pre_tax"
  | "taxes"
  | "fees_post_tax";

export interface PricingStage {
  key: PricingStageKey;
  label: string;
  total: string;
  items: StageLineItem[];
}

/**
 * The stages of a quote, split by where they belong relative to the subtotal.
 *
 * This split is the whole point of the helper. `fees_subtotal` is already
 * counted inside `breakdown.subtotal` — it exists as its own section only so a
 * fixed fee can be shown as its own row — while the other three are added on
 * top. Adding them all to the subtotal double-charges the fixed fees.
 */
export interface ResolvedPricingStages {
  /** Rows already inside the subtotal. Render above the Subtotal line. */
  beforeSubtotal: PricingStage[];
  /** Rows added on top of the subtotal. Render below it, in this order. */
  afterSubtotal: PricingStage[];
  /** True when the fleet configured any stage at all. */
  hasStages: boolean;
}

const STAGE_LABELS: Record<PricingStageKey, string> = {
  fees_subtotal: "Fees",
  fees_pre_tax: "Fees",
  taxes: "Taxes",
  fees_post_tax: "Fees",
};

const isPopulated = (stage?: StageBreakdown): stage is StageBreakdown =>
  Boolean(stage && stage.total && stage.items && stage.items.length > 0);

const toStage = (
  key: PricingStageKey,
  stage?: StageBreakdown,
): PricingStage | null =>
  isPopulated(stage)
    ? { key, label: STAGE_LABELS[key], total: stage.total, items: stage.items }
    : null;

const compact = (stages: (PricingStage | null)[]): PricingStage[] =>
  stages.filter((stage): stage is PricingStage => stage !== null);

/**
 * Resolves which pricing stages a breakdown actually has, in render order.
 *
 * A quote whose fleet configured no fees and no tax resolves to no stages at
 * all, so callers render exactly what they rendered before stages existed.
 */
export function resolvePricingStages(
  breakdown: StagedBreakdown | null | undefined,
): ResolvedPricingStages {
  if (!breakdown) {
    return { beforeSubtotal: [], afterSubtotal: [], hasStages: false };
  }

  const beforeSubtotal = compact([
    toStage("fees_subtotal", breakdown.fees_subtotal),
  ]);

  // Order is the requirement, not a preference: tax applies to the reservation
  // total first, and a processing fee is then computed on the amount that
  // actually hits the card.
  const afterSubtotal = compact([
    toStage("fees_pre_tax", breakdown.fees_pre_tax),
    toStage("taxes", breakdown.taxes),
    toStage("fees_post_tax", breakdown.fees_post_tax),
  ]);

  return {
    beforeSubtotal,
    afterSubtotal,
    hasStages: beforeSubtotal.length > 0 || afterSubtotal.length > 0,
  };
}

/** The shape a flat line item needs for the split to place it. */
export interface StageSplitItem {
  type?: string | null;
  metadata?: { percentage_base?: string } | null;
}

export interface SplitLineItems<T> {
  /** Rows to render as the itemised list, above the Subtotal line. */
  subtotalItems: T[];
  /** Stage sections already inside the subtotal. */
  beforeSubtotal: PricingStage[];
  /** Stage sections added on top of the subtotal, in this order. */
  afterSubtotal: PricingStage[];
  /** True when the fleet configured any stage at all. */
  hasStages: boolean;
}

/**
 * Splits a flat line item list against the breakdown's stages, in one step.
 *
 * Surfaces that render `line_items` as a flat list need this: the list contains
 * every charge, but the subtotal only covers the stage 1 ones, so listing a tax
 * row above a Subtotal that excludes it reads as broken arithmetic.
 *
 * Filtering and re-rendering are deliberately one operation. They used to be
 * two — a predicate that dropped tax and percentage fees, and a separate stage
 * list that was supposed to put them back — and a breakdown carrying no stage
 * sections made those rows disappear from the page entirely. A row can now only
 * leave the list by appearing in the stage that renders it.
 *
 * The check is per stage, not a single "has any stage" flag: a breakdown
 * carrying `taxes` but no `fees_post_tax` keeps the post-tax fee in the list,
 * because nothing else would show it.
 */
export function splitLineItemsByStage<T extends StageSplitItem>(
  lineItems: readonly T[] | null | undefined,
  breakdown: StagedBreakdown | null | undefined,
): SplitLineItems<T> {
  const stages = resolvePricingStages(breakdown);
  const rendered = new Set(stages.afterSubtotal.map((stage) => stage.key));

  const subtotalItems = (lineItems ?? []).filter((item) => {
    const key = stageKeyFor(item);
    return key === null || !rendered.has(key);
  });

  return { subtotalItems, ...stages };
}

/**
 * Which after-subtotal stage would render this item, or null when it belongs
 * in the itemised list.
 *
 * A percentage fee is the one that moves: it declares a `percentage_base`,
 * which is exactly what "resolved against a later stage" means. Fixed fees
 * (including delivery) declare none and stay inside the subtotal.
 */
function stageKeyFor(item: StageSplitItem): PricingStageKey | null {
  if (item.type === "tax") return "taxes";
  if (item.type !== "fee") return null;

  switch (item.metadata?.percentage_base) {
    case "pre_tax":
      return "fees_pre_tax";
    case "post_tax":
      return "fees_post_tax";
    default:
      return null;
  }
}

/**
 * How a percentage item arrived at its amount, e.g. `"5% of $433.64"`.
 *
 * Returns null for fixed-amount items and for anything that did not report a
 * resolved base, so callers can render the description alone.
 */
export function describeStageBasis(
  item: Pick<StageLineItem, "metadata">,
  formatCurrency: (amount: string) => string,
): string | null {
  const rate = item.metadata?.rate;
  const base = item.metadata?.resolved_base_amount;

  if (!rate || !base) return null;

  return `${rate}% of ${formatCurrency(base)}`;
}

/**
 * Label for a line item, preferring what the server described it as.
 *
 * Every consumer had reinvented `description ?? subtype ?? type`, and the
 * subtype fallback rendered `damage_excess` verbatim.
 */
export function getLineItemLabel(item: {
  type?: string | null;
  subtype?: string | null;
  description?: string | null;
}): string {
  if (item.description) return item.description;
  if (item.subtype) return humanize(item.subtype);
  return item.type ? humanize(item.type) : "";
}

const humanize = (value: string): string =>
  value
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
